// Funnel-wizard setup-status hook (FF-15 / MIS-998).
//
// ONE shared React Query wrapper around FF-8's computed checklist —
// `GET /api/domains/:domainId/setup-status` — keyed `['setup-status', domainId]`
// so every wizard surface (the shell, the step bodies FF-16..FF-20 add, and the
// FF-21 review checklist) reads the SAME cache entry instead of bespoke
// per-step fetches. Wizard resumability comes from THIS server truth; nothing
// about progress is persisted client-side.
//
// The DTOs below are HAND-WRITTEN (not `InferResponseType`) because the route's
// in-handler `gateDomainRole` returns a bare `Response`, which degrades the RPC
// response union to `unknown` (the same reason LanderRoutingSection casts the
// phone-pools body and ad/types.ts hand-writes its binding DTOs). They mirror
// the worker's exported `DomainSetupStatus`/`LanderNumberRouting` interfaces
// (apps/worker/src/routes/setup_status.ts) FIELD-FOR-FIELD — change them
// together or not at all.

import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useApi } from '@/lib/api';

/** One lander's resolved number-routing mode for the checklist. */
export interface LanderNumberRouting {
  landerId: number;
  slug: string;
  /** 'pool' | 'static' | 'disabled' | 'trackdrive' (own config — NumberPoolMode) | 'default' (domain fallback) | 'missing'. */
  mode: string;
}

/** The computed per-domain setup checklist exactly as the worker returns it. */
export interface DomainSetupStatus {
  domainId: number;
  host: string;
  ready: boolean;
  completed: number;
  total: number;
  checks: {
    dns: boolean;
    ssl: boolean;
    pixel: boolean;
    capiCreds: boolean;
    trackdriveCreds: boolean;
    postbackToken: boolean;
    landers: { count: number; ok: boolean };
    numberRouting: { ok: boolean; perLander: LanderNumberRouting[] };
    fbBinding: { ok: boolean; boundLanders: number };
  };
}

/**
 * The shared query key — exported so other surfaces (e.g. step bodies that
 * mutate funnel pieces) can invalidate the SAME cache slot the wizard reads.
 */
export function setupStatusQueryKey(domainId: number | undefined) {
  return ['setup-status', domainId] as const;
}

/**
 * Invalidate EVERY setup-status cache entry a wizard mutation can stale
 * (QF-8 / MIS-1027): the per-domain key `['setup-status', domainId]` the
 * wizard reads, AND — via the `['setup-status', 'all']` prefix — the
 * tenant-keyed aggregate `['setup-status', 'all', <tenantId>]` the domains +
 * landers list badges read (SetupStatusBadge's `useSetupStatuses`). Wizard
 * step mutations MUST call this instead of invalidating the per-domain key
 * alone, or the list badges stay stale until their own refetch. Returns the
 * combined promise so callers that gate on the refreshed frontier (e.g.
 * DnsWaitStep's auto-advance) can await it.
 */
export function invalidateSetupStatus(
  queryClient: QueryClient,
  domainId: number | undefined,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: setupStatusQueryKey(domainId) }),
    // Prefix match: covers ['setup-status', 'all', <tenant>] for any tenant.
    queryClient.invalidateQueries({ queryKey: ['setup-status', 'all'] }),
  ]).then(() => undefined);
}

/**
 * Fetch the server-computed setup checklist for one domain. Disabled until a
 * domainId exists (wizard step 1 — nothing to derive yet).
 */
export function useSetupStatus(domainId: number | undefined) {
  const api = useApi();

  return useQuery({
    queryKey: setupStatusQueryKey(domainId),
    enabled: domainId !== undefined,
    queryFn: async (): Promise<DomainSetupStatus> => {
      // `enabled` already gates this; the throw narrows for TS and guards a
      // manual `refetch()` while no domain is selected.
      if (domainId === undefined) throw new Error('domainId required');
      const res = await api.api.domains[':domainId']['setup-status'].$get({
        param: { domainId: String(domainId) },
      });
      if (!res.ok) throw new Error(`Failed to load setup status (${res.status})`);
      // Cast (not infer): see the file header on the degraded response union.
      const data = (await res.json()) as { status: DomainSetupStatus };
      return data.status;
    },
  });
}
